import React from 'react';

import ExplorerIcon from 'components/FileViewer/ExplorerIcon';
import { ExplorerFolder, pluralize } from 'helper/products/fileExplorer';

import './index.scss';

interface TreeProps {
  root: ExplorerFolder;
  activeKey: string;
  expanded: Set<string>;
  onToggle: (key: string) => void;
  onOpen: (path: string[]) => void;
}

const subfoldersOf = (folder: ExplorerFolder) => folder.children.filter(
  (child): child is ExplorerFolder => child.kind === 'folder',
);

function TreeBranch({
  folders, nested, activeKey, expanded, onToggle, onOpen,
}: Omit<TreeProps, 'root'> & { folders: ExplorerFolder[]; nested: boolean }) {
  return (
    <ul className={nested ? 'file-explorer-tree-group' : 'file-explorer-tree-root'}>
      {folders.map((folder) => {
        const isOpen = folder.hasSubfolders && expanded.has(folder.key);
        const isActive = folder.key === activeKey;
        return (
          <li key={folder.key} className="file-explorer-tree-item">
            <div className={`file-explorer-tree-row${isActive ? ' is-active' : ''}`}>
              {folder.hasSubfolders ? (
                <button
                  type="button"
                  className={`file-explorer-tree-toggle${isOpen ? ' is-open' : ''}`}
                  aria-label={`${isOpen ? 'Recolher' : 'Expandir'} ${folder.name}`}
                  aria-expanded={isOpen}
                  onClick={() => onToggle(folder.key)}
                >
                  <ExplorerIcon name="chevronRight" size={11} strokeWidth={2.8} />
                </button>
              ) : (
                <span className="file-explorer-tree-toggle is-leaf" aria-hidden="true" />
              )}
              <button
                type="button"
                className="file-explorer-tree-folder"
                aria-label={`${folder.name} (${pluralize(folder.fileCount, 'arquivo', 'arquivos')})`}
                aria-current={isActive ? 'true' : undefined}
                title={folder.path.join(' / ')}
                onClick={() => onOpen(folder.path)}
              >
                <ExplorerIcon
                  name={isOpen || isActive ? 'folderOpen' : 'folder'}
                  size={16}
                  strokeWidth={1.8}
                  className="file-explorer-tree-icon"
                />
                <span className="file-explorer-tree-label">{folder.name}</span>
                <span className="file-explorer-tree-count">{folder.fileCount.toLocaleString('pt-BR')}</span>
              </button>
            </div>
            {isOpen && (
              <TreeBranch
                folders={subfoldersOf(folder)}
                nested
                activeKey={activeKey}
                expanded={expanded}
                onToggle={onToggle}
                onOpen={onOpen}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function FolderTree({
  root, activeKey, expanded, onToggle, onOpen,
}: TreeProps) {
  const folders = subfoldersOf(root);
  if (!folders.length) return null;

  return (
    <nav className="file-explorer-tree" aria-label="Árvore de pastas">
      <h2 className="file-explorer-tree-title">Pastas</h2>
      <TreeBranch
        folders={folders}
        nested={false}
        activeKey={activeKey}
        expanded={expanded}
        onToggle={onToggle}
        onOpen={onOpen}
      />
    </nav>
  );
}
